import { CareColoredIcon } from '@village/icons';
import { Text } from '@village/ui';
import { atcb_action } from 'add-to-calendar-button';
import { SelectedAppointmentDetailsCard } from 'components/selected-appointment-details-card';
import { LINKS } from 'constants/links';
import { useBooking, useScrollToTop, useCalendarEntry, useCaptureCountlyEvent } from 'hooks';
import appInMobileScreenGraphic from 'images/app-in-mobile-screen.png';
import appStoreImage from 'images/app-store-badge.png';
import appleIcon from 'images/apple-icon.png';
import googleIcon from 'images/google-icon.png';
import googlePlayStoreImage from 'images/google-play-badge.png';
import microsoftIcon from 'images/microsoft-icon.png';
import { appConfig } from 'modules/app-config';
import { Countly } from 'modules/countly';
import { ReactElement, useEffect, useTransition } from 'react';
import { Navigate, useNavigate } from 'react-router';

import * as Styled from './styles';

type Platform = 'Google' | 'Apple' | 'Microsoft';

interface EmailClient {
    platform: Platform;
    image: ReactElement<HTMLImageElement>;
}

const BookingConfirmationPage = () => {
    useScrollToTop();
    const navigate = useNavigate();
    const startTransition = useTransition()[1];
    const { booking, setBookingField } = useBooking();
    const { addCountlyEvent } = useCaptureCountlyEvent();
    const { calendarButtonConfig, googleCalendarLink } = useCalendarEntry();

    const emailClientButtons: EmailClient[] = [
        {
            platform: 'Apple',
            image: <img src={appleIcon} width="24" />,
        },
        {
            platform: 'Google',
            image: <img src={googleIcon} width="24" />,
        },
        {
            platform: 'Microsoft',
            image: <img src={microsoftIcon} width="24" />,
        },
    ];

    useEffect(() => {
        window.onpopstate = () => {
            startTransition(() => {
                setBookingField({ appointment: undefined });
            });

            navigate('/select-appointment', { replace: true });
        };
    }, [navigate, setBookingField]);

    // This is a test survey that will only show in non-production environments.
    // We are setting it up so that security can vet it in sandbox
    // Following the security review, we will remove it or replace it with the actual survey.
    useEffect(() => {
        if (appConfig.postBookingSurvey) {
            Countly.sdk.feedback.showSurvey(appConfig.postBookingSurvey);
        }
    }, []);

    const redirectToPlatformAppStore = (platform: Platform) => {
        const mobilePlatformLink =
            platform === 'Google' ? LINKS.villageMedical.googlePlayStoreUrl : LINKS.villageMedical.appleStoreUrl;

        addCountlyEvent({
            key: 'downloadApp',
            segmentation: {
                downloadedMobilePlatform: platform,
            },
        });

        // Opening a new window to make sure we are capturing data in countly.
        window.open(mobilePlatformLink, '_blank', 'noopener,noreferrer');
    };

    const handleEmailPlatformButton = (platform: Platform): void => {
        addCountlyEvent({
            key: 'addCalendar',
            segmentation: {
                calendarPlatform: platform,
            },
        });

        // While all email clients use the ICS file, the preferred mechanism for the Google (gmail) platform is to use a link instead of a ICS file.
        if (calendarButtonConfig && platform !== 'Google') {
            atcb_action(calendarButtonConfig);
            return;
        }

        // Currently using window.open in favor of a native <A> tag, as we need to capture the event for countly reporting.
        // window.open() might not be the best solution for redirecting to an external URL though.
        if (googleCalendarLink && platform === 'Google') {
            window.open(googleCalendarLink, '_blank', 'noopener,noreferrer');
        }
    };

    if (!booking.appointment) {
        return <Navigate to="/select-appointment" replace={true} />;
    }

    return (
        <Styled.Container>
            <Styled.Header>
                <CareColoredIcon customSize={6} />
                <Styled.Title type="h3">Appointment Confirmed</Styled.Title>
                <Styled.VisitTypeNote type="body1">
                    {booking.reason?.is_in_person
                        ? `We've sent an appointment confirmation to ${booking?.patientInformation?.email}`
                        : 'Your provider will send you a link to access the appointment via email or text message shortly before the visit.'}
                </Styled.VisitTypeNote>
            </Styled.Header>

            <Styled.Wrapper>
                <SelectedAppointmentDetailsCard mode="CONFIRMATION" />
                <Styled.RoundedWrapper>
                    <Styled.RoundedWrapperTitle type="sub3">Prepare for your visit</Styled.RoundedWrapperTitle>
                    {booking.reason?.is_in_person && (
                        <Styled.RoundedWrapperList>
                            <Styled.RoundedWrapperListTitle type="h6">Prepare for your visit</Styled.RoundedWrapperListTitle>
                            <Styled.RoundedWrapperListItem>
                                <Text type="body1">Please arrive 15 minutes early.</Text>
                            </Styled.RoundedWrapperListItem>
                            <Styled.RoundedWrapperListItem>
                                <Text type="body1">
                                    Remember to bring updated insurance card, a method of payment, and list of medications.
                                </Text>
                            </Styled.RoundedWrapperListItem>
                            <Styled.RoundedWrapperListItem>
                                <Text type="body1">Make a list of any questions you have for your doctor.</Text>
                            </Styled.RoundedWrapperListItem>
                        </Styled.RoundedWrapperList>
                    )}

                    <Styled.RoundedWrapperList>
                        <Styled.RoundedWrapperListTitle type="h6">
                            No-show/Late Cancellation policy
                        </Styled.RoundedWrapperListTitle>
                        <Styled.RoundedWrapperListItem>
                            <Text type="body1">
                                To stay on schedule and provide the best care, we have a{' '}
                                <Styled.Link href="https://www.villagemedical.com/compliance-and-privacy">
                                    No Show/Late Cancellation Policy
                                </Styled.Link>
                                . Cancelling within 24 hours or missing your appointment may result in a fee. Thank you for
                                understanding!
                                <br />
                                <br />
                                *Medicaid patients are not subject to these fees.
                            </Text>
                        </Styled.RoundedWrapperListItem>
                    </Styled.RoundedWrapperList>
                </Styled.RoundedWrapper>

                {calendarButtonConfig || googleCalendarLink ? (
                    <Styled.RoundedWrapper>
                        <Styled.RoundedWrapperTitle type="sub3">Add appointment to calendar</Styled.RoundedWrapperTitle>
                        <Text type="body1">Add upcoming appointments to your calendar for a timely reminder.</Text>
                        {emailClientButtons.map((emailClientButton) => (
                            <Styled.EmailPlatformButton
                                key={`email-client-${emailClientButton.platform}`}
                                startIcon={emailClientButton.image}
                                variant="secondary"
                                size="medium"
                                onClick={() => handleEmailPlatformButton(emailClientButton.platform)}
                            >
                                {emailClientButton.platform === 'Microsoft' ? 'Outlook' : emailClientButton.platform}
                            </Styled.EmailPlatformButton>
                        ))}
                    </Styled.RoundedWrapper>
                ) : null}
                <Styled.RoundedWrapperPlatformStoreAnnouncement>
                    <div>
                        <Styled.RoundedWrapperTitle type="sub3">
                            Manage your appointment with the Village Medical App
                        </Styled.RoundedWrapperTitle>
                        <Styled.RoundedWrapperPlatformStoreAnnouncementBody type="body1">
                            Download now to breeze through your pre-visit paperwork. After your visit, quickly reference your
                            visit summary and message your care team 24/7 with any questions.
                        </Styled.RoundedWrapperPlatformStoreAnnouncementBody>
                        <Styled.MobilePlatformButton onClick={() => redirectToPlatformAppStore('Google')}>
                            <Styled.PlatformButtonImage alt="Get it on Google Play" src={googlePlayStoreImage} />
                        </Styled.MobilePlatformButton>
                        <Styled.MobilePlatformButton onClick={() => redirectToPlatformAppStore('Apple')}>
                            <Styled.PlatformButtonImage alt="Download on the App Store" src={appStoreImage} />
                        </Styled.MobilePlatformButton>
                    </div>
                    <img src={appInMobileScreenGraphic} width="194" />
                </Styled.RoundedWrapperPlatformStoreAnnouncement>
            </Styled.Wrapper>
        </Styled.Container>
    );
};

export { BookingConfirmationPage };
